import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { hrApi } from "../../services/hrApi";
import HRGuideModal from "../../components/hr/HRGuideModal";
import HRStatCard from "../../components/hr/HRStatCard";
import { useAccess } from "../../context/AccessContext";
import { hasModule, hasPermission } from "../../utils/accessHelpers";


function StatCard({ title, value, onClick }) {
  return (
    <div
      onClick={onClick}
      className="bg-white rounded-2xl shadow-sm border p-4 cursor-pointer hover:shadow-md transition"
    >
      <div className="text-sm text-gray-500">{title}</div>
      <div className="text-2xl font-semibold mt-2">{value ?? 0}</div>
    </div>
  );
}

export default function HRDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({
    totals: {},
    byDepartment: [],
    expiringContracts: [],
    expiringDocuments: [],
    onboardingOpen: [],
  });
  const [showGuide, setShowGuide] = useState(false);

  const { access, loading: accessLoading } = useAccess();

  const canSeeHR = hasModule(access, "hr");
  const canReadPeople = hasPermission(access, "hr.people.read");
  const canWriteStructure = hasPermission(access, "hr.structure.write");

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        const res = await hrApi.getDashboard();
        if (!mounted) return;
        setData(res.data?.data || {});
      } catch (err) {
        console.error("Failed to load HR dashboard:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  const totals = data.totals || {};

  if (accessLoading) {
  return <div className="p-6">Loading access...</div>;
}

if (!canSeeHR) {
  return <div className="p-6 text-red-600">Access denied.</div>;
}

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
  <div>
    <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
  HR Dashboard
</h1>
<p className="text-sm text-gray-500 dark:text-gray-400">
  Pregled ljudi, ugovora i HR statusa.
</p>
  </div>

  <div className="flex flex-wrap gap-3">

  {canReadPeople && (
    <button
      onClick={() => navigate("/hr/people")}
      className="px-4 py-2 rounded-xl bg-black text-white dark:bg-white dark:text-black"
    >
      People Directory
    </button>
  )}

  {canSeeHR && (
    <button
      onClick={() => navigate("/hr/org-chart")}
      className="px-4 py-2 rounded-xl bg-blue-600 text-white"
    >
      Org Chart
    </button>
  )}

  {canWriteStructure && (
    <button
      onClick={() => navigate("/hr/positions")}
      className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      Positions
    </button>
  )}

  {canWriteStructure && (
    <button
      onClick={() => navigate("/hr/settings")}
      className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      HR Settings
    </button>
  )}

  {canSeeHR && (
    <button
      onClick={() => setShowGuide(true)}
      className="px-4 py-2 rounded-xl border border-gray-300 bg-white text-black dark:bg-neutral-900 dark:text-white dark:border-neutral-700"
    >
      ℹ HR Guide
    </button>
  )}

</div>
</div>

      {loading ? (
        <div className="text-sm text-gray-500">Učitavanje...</div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            <HRStatCard title="Total people" value={totals.total_people} />
            <HRStatCard title="Active people" value={totals.active_people} />
            <HRStatCard title="Active employees" value={totals.active_employees} />
            <HRStatCard title="Active contractors" value={totals.active_contractors} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 p-4 text-gray-900 dark:text-white">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">People by department</h2>
              <div className="space-y-3">
                {(data.byDepartment || []).map((row) => (
                  <div key={row.id} className="flex items-center justify-between border-b border-gray-200 dark:border-neutral-800 pb-2">
                    <div>{row.name}</div>
                    <div className="font-medium">{row.people_count}</div>
                  </div>
                ))}
                {!data.byDepartment?.length && (
                  <div className="text-sm text-gray-500">Nema podataka.</div>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 p-4 text-gray-900 dark:text-white">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Expiring contracts</h2>
              <div className="space-y-3">
                {(data.expiringContracts || []).map((row) => (
                  <div key={row.id} className="border-b border-gray-200 dark:border-neutral-800 pb-2">
                    <div className="font-medium">{row.person_name}</div>
                    <div className="text-sm text-gray-500">
                      {row.department_name || "-"} / {row.team_name || "-"}
                    </div>
                    <div className="text-sm">End date: {row.end_date}</div>
                  </div>
                ))}
                {!data.expiringContracts?.length && (
                  <div className="text-sm text-gray-500">Nema ugovora koji uskoro ističu.</div>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 p-4 text-gray-900 dark:text-white">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Expiring documents</h2>
              <div className="space-y-3">
                {(data.expiringDocuments || []).map((row) => (
                  <div key={row.id} className="border-b border-gray-200 dark:border-neutral-800 pb-2">
                    <div className="font-medium">{row.title}</div>
                    <div className="text-sm text-gray-500">{row.person_name}</div>
                    <div className="text-sm">Expiry: {row.expiry_date}</div>
                  </div>
                ))}
                {!data.expiringDocuments?.length && (
                  <div className="text-sm text-gray-500">Nema dokumenata koji uskoro ističu.</div>
                )}
              </div>
            </div>

            <div className="bg-white dark:bg-neutral-900 rounded-2xl shadow-sm border border-gray-200 dark:border-neutral-800 p-4 text-gray-900 dark:text-white">
              <h2 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Onboarding open tasks</h2>
              <div className="space-y-3">
                {(data.onboardingOpen || []).map((row, index) => (
                  <div key={`${row.person_id}-${index}`} className="flex items-center justify-between border-b border-gray-200 dark:border-neutral-800 pb-2">
                    <div>Person #{row.person_id}</div>
                    <div className="font-medium">{row.open_tasks}</div>
                  </div>
                ))}
                {!data.onboardingOpen?.length && (
                  <div className="text-sm text-gray-500">Nema otvorenih onboarding taskova.</div>
                )}
              </div>
            </div>
          </div>
        </>
      )}

<HRGuideModal open={showGuide} onClose={() => setShowGuide(false)} />

    </div>
  );
}